import React, { useCallback, useEffect, useRef, useState } from 'react';
import { registerActionToastHandlers } from './actionToastBus';
import styles from './ActionToast.module.scss';

/**
 * Global host for action toasts (message + optional action button).
 * Mount once near the app root. Call showActionToast() from anywhere.
 */
const ActionToastHost = () => {
  const [toast, setToast] = useState(null);
  const timeoutRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearTimer();
    setToast(null);
  }, [clearTimer]);

  const show = useCallback(
    ({ message, actionLabel, onAction, timeout = 5000 }) => {
      clearTimer();
      setToast({
        message,
        actionLabel: actionLabel || null,
        onAction: typeof onAction === 'function' ? onAction : null,
      });
      if (timeout > 0 && typeof setTimeout !== 'undefined') {
        timeoutRef.current = setTimeout(() => {
          setToast(null);
          timeoutRef.current = null;
        }, timeout);
      }
    },
    [clearTimer]
  );

  useEffect(() => {
    return registerActionToastHandlers({ show, hide });
  }, [show, hide]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  if (!toast) {
    return null;
  }

  const handleAction = () => {
    const { onAction } = toast;
    hide();
    if (onAction) {
      onAction();
    }
  };

  return (
    <div className={styles.toast} role="status" data-testid="action-toast">
      <p className={styles.message}>{toast.message}</p>
      {toast.actionLabel ? (
        <button
          type="button"
          className={styles.action}
          onClick={handleAction}
          data-testid="action-toast-action"
        >
          {toast.actionLabel}
        </button>
      ) : null}
    </div>
  );
};

export default ActionToastHost;
